import { useLocation } from "react-router-dom";

const breadcrumbMap: Record<string, string> = {
  "/": "Dashboard",
  "/projetos": "Projetos",
  "/cta": "CTA Arbóreo",
  "/mapa": "Mapa Arbóreo",
  "/vistorias": "Vistorias",
  "/ambiental": "Ambiental",
  "/ambiental/areas-verdes": "Áreas Verdes",
  "/ambiental/ucs": "Unidades de Conservação",
  "/carbono": "Carbono",
  "/documentos": "Documentos e Fontes",
  "/partes": "Partes Envolvidas",
  "/responsabilidades": "Responsabilidades e Restrições",
  "/relatorios": "Relatórios",
  "/configuracoes": "Configurações",
};

interface TopbarProps {
  onMenuClick: () => void;
}

export default function Topbar({ onMenuClick }: TopbarProps) {
  const location = useLocation();
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const currentLabel = breadcrumbMap[location.pathname] || "Página";

  const breadcrumbs = [
    { label: "FloraIQ", path: "/" },
    ...(pathSegments.length > 0
      ? pathSegments.reduce<{ label: string; path: string }[]>((acc, seg, idx) => {
          const path = "/" + pathSegments.slice(0, idx + 1).join("/");
          const label = breadcrumbMap[path] || seg;
          acc.push({ label, path });
          return acc;
        }, [])
      : []),
  ];

  return (
    <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-background-200/70">
      <div className="flex items-center justify-between h-16 px-4 md:px-6">
        {/* Left: hamburger + breadcrumbs */}
        <div className="flex items-center gap-2 min-w-0">
          {/* Hamburger mobile */}
          <button
            onClick={onMenuClick}
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer shrink-0"
            aria-label="Abrir menu"
          >
            <i className="ri-menu-line text-foreground-600 text-lg"></i>
          </button>

          {/* Breadcrumbs */}
          <div className="flex items-center gap-1.5 text-sm overflow-hidden">
            {breadcrumbs.map((crumb, idx) => (
              <span key={crumb.path} className="flex items-center gap-1.5 min-w-0">
                {idx > 0 && (
                  <i className="ri-arrow-right-s-line text-foreground-400 text-xs shrink-0"></i>
                )}
                {idx === breadcrumbs.length - 1 ? (
                  <span className="text-foreground-900 font-medium truncate">{crumb.label}</span>
                ) : (
                  <a
                    href={crumb.path}
                    className="text-foreground-500 hover:text-foreground-700 transition-colors whitespace-nowrap hidden sm:inline"
                  >
                    {crumb.label}
                  </a>
                )}
              </span>
            ))}
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center gap-2 md:gap-3 shrink-0">
          {/* Search */}
          <div className="relative hidden md:block">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              type="text"
              placeholder="Buscar árvores, projetos..."
              className="w-48 lg:w-64 pl-9 pr-3 py-2 text-sm bg-background-100 border border-background-200/70 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300 text-foreground-700 placeholder:text-foreground-400 transition-all"
            />
          </div>

          {/* Mobile search icon */}
          <button className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer" aria-label="Buscar">
            <i className="ri-search-line text-foreground-600 text-lg"></i>
          </button>

          {/* Notifications */}
          <button className="relative w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer" aria-label="Notificações">
            <i className="ri-notification-3-line text-foreground-600 text-lg"></i>
            <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-danger-500 border border-white"></span>
          </button>

          {/* User */}
          <div className="flex items-center gap-2.5 pl-2 md:pl-3 border-l border-background-200/70">
            <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
              <span className="text-primary-700 text-xs font-semibold">WC</span>
            </div>
            <div className="hidden md:block text-right">
              <p className="text-sm font-medium text-foreground-900 leading-tight">Weliton Cena</p>
              <p className="text-xs text-foreground-500 leading-tight">Coordenador Técnico</p>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}